import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControlLabel,
  Alert,
  IconButton,
  Avatar,
} from "@mui/material";
import {
  Close as CloseIcon,
  Send as SendIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import ContactApiService from "../services/ContactApiService";
import MessageTemplateService from "../services/MessageTemplateService";

const CreateBroadcastDialog = ({ open, onClose, onSave, editBroadcast }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    broadcastName: "",
    selectedTemplate: "",
    scheduleDate: new Date(),
    sendNow: false,
    selectedContacts: [],
  });

  // State for template variable values
  const [templateVariables, setTemplateVariables] = useState({ header: {}, body: {}, footer: {} });
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [approvedTemplates, setApprovedTemplates] = useState([]);

  // Mock data for contacts
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    ContactApiService.getContactsDetails().then((res) => setContacts(res));
    MessageTemplateService.getAllTemplates().then((res) => setApprovedTemplates(res));
  }, []);

  // Prefill form when editing
  useEffect(() => {
    if (open && editBroadcast) {
      // Set form fields
      setFormData({
        broadcastName: editBroadcast.attrs.broadcastName || "",
        selectedTemplate: editBroadcast.attrs.selectedTemplate || "",
        scheduleDate: editBroadcast.attrs.scheduleDate ? new Date(editBroadcast.attrs.scheduleDate) : new Date(),
        sendNow: !!editBroadcast.attrs.sendNow,
        selectedContacts: editBroadcast.attrs.selectedContacts || [],
      });

      // Set template variables
      setTemplateVariables({
        header: (editBroadcast.attrs.templateVariables && editBroadcast.attrs.templateVariables.header) || {},
        body: (editBroadcast.attrs.templateVariables && editBroadcast.attrs.templateVariables.body) || {},
        footer: (editBroadcast.attrs.templateVariables && editBroadcast.attrs.templateVariables.footer) || {},
      });

      // Set contacts selection (add isSelected: true to contacts in selectedContacts)
      if (editBroadcast.attrs.selectedContacts) {
        const selectedIds = editBroadcast.attrs.selectedContacts.map(c => c.attrs && c.attrs.id);
        setContacts((prev) =>
          prev.map((contact) =>
            selectedIds.includes(contact.attrs.id)
              ? { ...contact, isSelected: true }
              : { ...contact, isSelected: false }
          )
        );
      }

      setActiveStep(0);
    } else if (open && !editBroadcast) {
      // Reset on new
      setFormData({
        broadcastName: "",
        selectedTemplate: "",
        scheduleDate: new Date(),
        sendNow: false,
        selectedContacts: [],
      });
      setTemplateVariables({ header: {}, body: {}, footer: {} });
      setContacts((prev) => prev.map((c) => ({ ...c, isSelected: false })));
      setActiveStep(0);
    }
  }, [open, editBroadcast]);

  const steps = ["Broadcast Setup", "Contact Selection"];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    // Reset variable values when template changes
    if (field === "selectedTemplate") {
      const selected = approvedTemplates.find(t => t.getId() === value);
      let variableSamples = { header: {}, body: {}, footer: {} };
      try {
        variableSamples = selected ? JSON.parse(selected.attrs.variableSamples) : { header: {}, body: {}, footer: {} };
      } catch (e) {}
      setTemplateVariables({
        header: Object.fromEntries(Object.keys(variableSamples.header || {}).map(k => [k, ""])),
        body: Object.fromEntries(Object.keys(variableSamples.body || {}).map(k => [k, ""])),
        footer: Object.fromEntries(Object.keys(variableSamples.footer || {}).map(k => [k, ""])),
      });
    }
  };

  const validateStep1 = () => {
    const newErrors = {};

    if (!formData.broadcastName.trim()) {
      newErrors.broadcastName = "Broadcast name is required";
    }

    if (!formData.selectedTemplate) {
      newErrors.selectedTemplate = "Please select a template";
    }

    if (!formData.sendNow && !formData.scheduleDate) {
      newErrors.scheduleDate = "Please select a schedule date";
    }

    // Validate template variable values
    const selected = approvedTemplates.find(t => t.getId() === formData.selectedTemplate);
    let variableSamples = { header: {}, body: {}, footer: {} };
    try {
      variableSamples = selected ? JSON.parse(selected.attrs.variableSamples) : { header: {}, body: {}, footer: {} };
    } catch (e) {}
    ["header", "body", "footer"].forEach(section => {
      if (variableSamples[section]) {
        Object.keys(variableSamples[section]).forEach(key => {
          if (!templateVariables[section][key] || !templateVariables[section][key].trim()) {
            newErrors[`var_${section}_${key}`] = `Please enter value for variable {{${key}}} in ${section}`;
          }
        });
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (activeStep === 0 && validateStep1()) {
      setActiveStep(1);
    }
  };

  const handleBack = () => {
    setActiveStep(0);
  };

  const handleContactSelection = (contactId) => {
    setContacts((prev) =>
      prev.map((contact) =>
        contact.attrs.id === contactId
          ? { ...contact, isSelected: !contact.isSelected }
          : contact
      )
    );
  };

  const handleSelectAll = (event) => {
    const isChecked = event.target.checked;
    setContacts((prev) =>
      prev.map((contact) => ({ ...contact, isSelected: isChecked }))
    );
  };

  const handleSave = () => {
    const selectedContacts = contacts.filter((contact) => contact.isSelected);
    const broadcastData = {
      ...formData,
      selectedContacts: selectedContacts,
      templateVariables,
    };

    console.log("Saving broadcast:", broadcastData);
    onSave(broadcastData);
    handleClose();
  };

  const handleClose = () => {
    setActiveStep(0);
    setFormData({
      broadcastName: "",
      selectedTemplate: "",
      scheduleDate: new Date(),
      sendNow: false,
      selectedContacts: [],
    });
    setErrors({});
    setSearchQuery("");
    setContacts((prev) =>
      prev.map((contact) => ({ ...contact, isSelected: false }))
    );
    onClose();
  };

  const getTagColor = (tag) => {
    return tag || "#757575";
  };
console.log('contacts', contacts);

  const filteredContacts = contacts.filter(
    (contact) =>
      contact.attrs.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.attrs.phoneNumber.includes(searchQuery)
  );

  const selectedContactsCount = contacts.filter(
    (contact) => contact.isSelected
  ).length;
  const isAllSelected =
    contacts.length > 0 && selectedContactsCount === contacts.length;
  const isIndeterminate =
    selectedContactsCount > 0 && selectedContactsCount < contacts.length;

  const renderStep1 = () => {
    // Get selected template's variableSamples
    const selected = approvedTemplates.find(t => t.getId() === formData.selectedTemplate);
    let variableSamples = { header: {}, body: {}, footer: {} };
    try {
      variableSamples = selected ? JSON.parse(selected.attrs.variableSamples) : { header: {}, body: {}, footer: {} };
    } catch (e) {}

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#333", mb: 2 }}
            >
              Broadcast Details
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Broadcast Name"
                value={formData.broadcastName}
                onChange={(e) =>
                  handleInputChange("broadcastName", e.target.value)
                }
                error={!!errors.broadcastName}
                helperText={errors.broadcastName}
                fullWidth
                size="small"
                placeholder="Enter broadcast name"
              />

              <FormControl
                fullWidth
                size="small"
                error={!!errors.selectedTemplate}
              >
                <InputLabel>Select Template</InputLabel>
                <Select
                  value={formData.selectedTemplate}
                  onChange={(e) =>
                    handleInputChange("selectedTemplate", e.target.value)
                  }
                  label="Select Template"
                >
                  {approvedTemplates.map((template) => (
                    <MenuItem key={template.getId()} value={template.getId()}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {template.getTemplateName()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {template.getCategory()} • {template.getLanguage()}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {errors.selectedTemplate && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.5 }}
                  >
                    {errors.selectedTemplate}
                  </Typography>
                )}
              </FormControl>

              {/* Variable input fields for header/body/footer */}
              {["header", "body", "footer"].map((section) => (
                variableSamples[section] && Object.keys(variableSamples[section]).length > 0 && (
                  <Box key={section} sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                      {section.charAt(0).toUpperCase() + section.slice(1)} Variables
                    </Typography>
                    {Object.keys(variableSamples[section]).map((key) => (
                      <TextField
                        key={key}
                        label={`Value for {{${key}}}`}
                        value={templateVariables[section][key] || ""}
                        onChange={(e) =>
                          setTemplateVariables((prev) => ({
                            ...prev,
                            [section]: {
                              ...prev[section],
                              [key]: e.target.value,
                            },
                          }))
                        }
                        error={!!errors[`var_${section}_${key}`]}
                        helperText={errors[`var_${section}_${key}`] || `Enter value for {{${key}}}`}
                        fullWidth
                        size="small"
                      />
                    ))}
                  </Box>
                )
              ))}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.sendNow}
                    onChange={(e) =>
                      handleInputChange("sendNow", e.target.checked)
                    }
                    size="small"
                  />
                }
                label="Send Now"
                sx={{ alignSelf: "flex-start" }}
              />

              {!formData.sendNow && (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    label="Schedule Date & Time"
                    value={formData.scheduleDate}
                    onChange={(newValue) =>
                      handleInputChange("scheduleDate", newValue)
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        error={!!errors.scheduleDate}
                        helperText={errors.scheduleDate}
                      />
                    )}
                    minDateTime={new Date()}
                  />
                </LocalizationProvider>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const renderStep2 = () => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Card sx={{ border: "1px solid #e0e0e0", borderRadius: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "#333", mb: 2 }}
          >
            Select Recipients
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <SearchIcon
                    sx={{ color: "#9e9e9e", mr: 0.5, fontSize: 18 }}
                  />
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1.5,
                  backgroundColor: "#fafafa",
                  border: "1px solid #e0e0e0",
                  fontSize: "0.875rem",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    borderColor: "#d0d0d0",
                  },
                  "&.Mui-focused": {
                    backgroundColor: "#fff",
                    borderColor: "#1976d2",
                  },
                  "& fieldset": {
                    border: "none",
                  },
                },
              }}
            />

            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ maxHeight: 400 }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      padding="checkbox"
                      sx={{ backgroundColor: "#f5f5f5" }}
                    >
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                        sx={{ color: "#666" }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}
                    >
                      Name
                    </TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}
                    >
                      Phone Number
                    </TableCell>
                    <TableCell
                      sx={{ backgroundColor: "#f5f5f5", fontWeight: 600 }}
                    >
                      Tags
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredContacts?.map((contact) => (
                    <TableRow
                      key={contact.attrs.id}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f8f9fa",
                        },
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={contact.isSelected}
                          onChange={() => handleContactSelection(contact.attrs.id)}
                          sx={{ color: "#666" }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: "#1976d2",
                              fontSize: "0.875rem",
                            }}
                          >
                            <PersonIcon />
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {contact?.attrs.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{contact.attrs.phoneNumber}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {contact?.attrs.tags.map((tag) => (
                            <Chip
                              key={tag.getId()}
                              label={tag.getName()}
                              size="small"
                              sx={{
                                backgroundColor: `${getTagColor(tag.getColor())}15`,
                                color: getTagColor(tag.getColor()),
                                fontWeight: 500,
                                fontSize: "0.75rem",
                              }}
                            />
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {selectedContactsCount > 0 && (
              <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
                <Typography variant="body2">
                  {selectedContactsCount} contact(s) selected for broadcast
                </Typography>
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, borderBottom: "1px solid #e0e0e0" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, color: "#333" }}>
            Create Broadcast
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step Content */}
          {activeStep === 0 ? renderStep1() : renderStep2()}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: "1px solid #e0e0e0", gap: 1 }}>
        {activeStep === 1 && (
          <Button
            onClick={handleBack}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              px: 3,
            }}
          >
            Back
          </Button>
        )}

        <Box sx={{ flex: 1 }} />

        {activeStep === 0 ? (
          <Button
            onClick={handleNext}
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              px: 3,
              background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
              },
            }}
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SendIcon />}
            disabled={selectedContactsCount === 0}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              px: 3,
              background: "linear-gradient(135deg, #4caf50 0%, #45a049 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #45a049 0%, #3d8b40 100%)",
              },
              "&.Mui-disabled": {
                background: "#e0e0e0",
                color: "#999",
              },
            }}
          >
            Create Broadcast ({selectedContactsCount})
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateBroadcastDialog;
